import React, { forwardRef } from 'react';
import { PatternFormat } from 'react-number-format';
import BaseInput from './BaseInput';
import * as styles from './Input.module.css';

interface InputProps {
  id?: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string | null;
  floatedPlaceholder?: boolean;
  value?: string;
  fullWidth?: boolean;
  inputProps?: Record<string, unknown>;
  disabled?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  mask?: string | null;
  error?: boolean;
  errorMessage?: string;
  customPresentation?: (() => React.ReactNode) | null;
  variant?: 'outlined' | 'floated';
  success?: boolean;
  radius?: string | null;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id = undefined,
      onChange,
      onBlur = undefined,
      onFocus = undefined,
      placeholder = null,
      floatedPlaceholder = true,
      value = '',
      fullWidth = true,
      inputProps = {},
      disabled = false,
      startAdornment = null,
      endAdornment = null,
      mask = null,
      error = false,
      errorMessage = '',
      customPresentation = null,
      variant = 'outlined',
      success = false,
      radius = null,
    },
    ref,
  ) => {
    const renderInput = () => {
      const baseProps = {
        id,
        value,
        placeholder: floatedPlaceholder ? ' ' : (placeholder ?? undefined),
        onChange,
        onBlur,
        onFocus,
        disabled,
        ref,
        ...inputProps,
      };

      if (inputProps.readOnly && customPresentation && value) {
        return (
          <div id="input" {...(baseProps as any)}>
            {customPresentation()}
          </div>
        );
      }

      if (mask) {
        const { onChange: _, ...maskBaseProps } = baseProps;
        return (
          <PatternFormat
            {...(maskBaseProps as any)}
            format={mask}
            onValueChange={values => onChange(values.value)}
          />
        );
      }

      return <input {...(baseProps as any)} />;
    };

    return (
      <div className={styles.column} style={fullWidth ? { width: '100%' } : {}}>
        <BaseInput
          variant={variant}
          fullWidth={fullWidth}
          disabled={disabled}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          placeholder={placeholder}
          floatedPlaceholder={floatedPlaceholder}
          success={success}
          error={error}
          inputProps={inputProps}
          customPresentation={
            !!(inputProps.readOnly && customPresentation && value)
          }
          value={value}
          radius={radius}
        >
          {renderInput()}
        </BaseInput>
        {error && errorMessage && (
          <span className={styles.errorMessage}>{errorMessage}</span>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
